"use client";

import { useEffect } from "react";
import { usePathname } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";

function scrollToElementById(id: string) {
  if (!id) return;
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "auto", block: "start" });
  }
}

function scrollToHashTarget() {
  const hash = window.location.hash;
  if (!hash || hash.length <= 1) return;
  const id = decodeURIComponent(hash.slice(1));
  scrollToElementById(id);
}

function isHomeHref(href: string): boolean {
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return false;
    const p = url.pathname.replace(/\/$/, "") || "/";
    if (p === "/") return true;
    return routing.locales.some((loc) => p === `/${loc}`);
  } catch {
    return false;
  }
}

/** Retries: client navigations often omit hashchange; DOM may appear after route commit. */
function scheduleScrollToId(id: string) {
  scrollToElementById(id);
  const delays = [0, 16, 32, 64, 100, 150, 220, 320, 400, 500];
  for (const d of delays) {
    window.setTimeout(() => scrollToElementById(id), d);
  }
}

/**
 * Re-applies scroll for URLs like /en#methodology after hydration and late layout
 * (fonts, content-visibility). Also fixes App Router hash links that do not fire
 * hashchange when navigating to the same route with a new fragment (e.g. footer).
 */
export function HashScrollFix() {
  const pathname = usePathname();
  const isHome = pathname === "/" || pathname === "";

  useEffect(() => {
    if (!isHome) return;

    scrollToHashTarget();

    const t0 = window.setTimeout(scrollToHashTarget, 0);
    const t1 = window.setTimeout(scrollToHashTarget, 50);
    const t2 = window.setTimeout(scrollToHashTarget, 150);

    let rafInner = 0;
    const rafOuter = requestAnimationFrame(() => {
      rafInner = requestAnimationFrame(scrollToHashTarget);
    });

    const onLoad = () => scrollToHashTarget();
    window.addEventListener("load", onLoad);
    window.addEventListener("hashchange", scrollToHashTarget);
    window.addEventListener("popstate", scrollToHashTarget);

    let cancelled = false;
    const fonts = document.fonts?.ready;
    if (fonts) {
      void fonts.then(() => {
        if (!cancelled) scrollToHashTarget();
      });
    }

    return () => {
      cancelled = true;
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      cancelAnimationFrame(rafOuter);
      cancelAnimationFrame(rafInner);
      window.removeEventListener("load", onLoad);
      window.removeEventListener("hashchange", scrollToHashTarget);
      window.removeEventListener("popstate", scrollToHashTarget);
    };
  }, [isHome]);

  useEffect(() => {
    const onClickCapture = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const a = t.closest("a[href]");
      if (!a) return;
      const raw = a.getAttribute("href");
      if (!raw || !raw.includes("#")) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.button !== 0) return;
      const targetAttr = a.getAttribute("target");
      if (targetAttr && targetAttr !== "_self") return;

      let resolved: URL;
      try {
        resolved = new URL(raw, window.location.href);
      } catch {
        return;
      }
      if (!isHomeHref(resolved.href)) return;
      if (!resolved.hash || resolved.hash.length <= 1) return;

      const id = decodeURIComponent(resolved.hash.slice(1));
      if (!id) return;

      scheduleScrollToId(id);
    };

    document.addEventListener("click", onClickCapture, true);
    return () => document.removeEventListener("click", onClickCapture, true);
  }, []);

  return null;
}
